import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, subQuarters } from 'date-fns';
import { Calendar, Play } from 'lucide-react';
import { useEsmsStore } from '../../store/esms.store';
import DatePicker from "../DatePicker";
import { parse } from 'date-fns';

interface Preset {
  label: string;
  from: Date;
  to: Date;
}

export default function EsmsDashboardFilters({ isDark }: { isDark: boolean }) {
  const {
    fromDate,
    toDate,
    setDateRange,
    fetchDashboardData,
  } = useEsmsStore();

  const fromDateObj = parse(fromDate, 'yyyy-MM-dd', new Date());
  const toDateObj = parse(toDate, 'yyyy-MM-dd', new Date());

  const now = new Date();

  // Week presets
  const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
  const thisSunday = endOfWeek(now, { weekStartsOn: 1 });
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });
  const twoWeeksAgoMonday = new Date(thisMonday);
  twoWeeksAgoMonday.setDate(thisMonday.getDate() - 14);
  const twoWeeksAgoSunday = endOfWeek(twoWeeksAgoMonday, { weekStartsOn: 1 });

  const weekPresets: Preset[] = [
    { label: 'Tuần kia', from: twoWeeksAgoMonday, to: twoWeeksAgoSunday },
    { label: 'Tuần rồi', from: lastMonday, to: lastSunday },
    { label: 'Tuần này', from: thisMonday, to: thisSunday },
  ];

  // Month presets — specific months from Jan to current month
  const currentMonth = now.getMonth(); // 0-indexed (0=Jan, 3=Apr)
  const currentYear = now.getFullYear();
  const monthNames = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'];
  const specificMonthPresets: Preset[] = [];
  for (let m = 0; m <= currentMonth; m++) {
    const d = new Date(currentYear, m, 1);
    specificMonthPresets.push({
      label: monthNames[m],
      from: startOfMonth(d),
      to: endOfMonth(d),
    });
  }

  // Quarter presets
  const quarterPresets: Preset[] = [
    { label: 'Quý trước', from: startOfQuarter(subQuarters(now, 1)), to: endOfQuarter(subQuarters(now, 1)) },
    { label: 'Quý này', from: startOfQuarter(now), to: endOfQuarter(now) },
  ];

  const isPresetSelected = (preset: Preset) =>
    fromDate === format(preset.from, 'yyyy-MM-dd') &&
    toDate === format(preset.to, 'yyyy-MM-dd');

  const selectPreset = (preset: Preset) => {
    setDateRange(format(preset.from, 'yyyy-MM-dd'), format(preset.to, 'yyyy-MM-dd'));
  };

  const handleFromChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(format(date, 'yyyy-MM-dd'), toDate);
  };

  const handleToChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(fromDate, format(date, 'yyyy-MM-dd'));
  };

  const presetBtnClass = (active: boolean) =>
    `px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
      active
        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
        : isDark
          ? 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white hover:bg-white/[0.06]'
          : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  const separatorClass = `w-px h-4 ${isDark ? 'bg-white/10' : 'bg-gray-300'}`;

  return (
    <div className={`rounded-lg p-3 sm:p-4 space-y-3 ${isDark ? 'bg-white/[0.02] border border-white/[0.06]' : 'bg-white border border-gray-200 shadow-sm'}`}>
      {/* Row 1: Date pickers + Run button */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
        <div className="flex gap-3 sm:gap-4" style={{ overflow: 'visible' }}>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Từ ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={fromDateObj}
                onChange={handleFromChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày bắt đầu"
              />
            </div>
          </div>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Đến ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={toDateObj}
                onChange={handleToChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày kết thúc"
              />
            </div>
          </div>
        </div>

        <div className="sm:self-end sm:pb-0.5">
          <button
            onClick={() => fetchDashboardData(true)}
            className="px-5 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25 hover:-translate-y-0.5"
          >
            <Play className="w-4 h-4" />
            Chạy report
          </button>
        </div>
      </div>

      {/* Row 2: Presets — Tuần | Tháng | Quý */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs mr-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Chọn nhanh:</span>

        {weekPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}

        <div className={separatorClass} />

        {specificMonthPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}

        <div className={separatorClass} />

        {quarterPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
